import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import {
  ContextService,
  LabelsService,
  Language,
  provideLocaleId,
  provideOverlayContainer,
  provideTypology,
  withApimInterceptors,
} from '@bcef/ia-shared';
import { DATA_LAYER_SELECTORS_TOKEN, provideDataLayer } from '@difa/ia-data-layer';
import { provideTypologyFileUrl } from '@difa/ia-typology-files';
import { provideJsonConvert } from '@difa/ia-utils';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideState, provideStore, Store } from '@ngrx/store';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';

import { environment } from '@environments/environment';
import { successionEffects } from '@features/succession/store';
import { successionFeature } from '@features/succession/store/reducers';
import {
  selectDataLayerDocuments,
  selectDataLayerFuneralExpenses,
  selectDataLayerHasChildren,
  selectDataLayerHasNotary,
  selectDataLayerRelationshipWithDeceased,
} from '@features/succession/store/selectors';
import { DataLayerSelectors } from '@features/succession/types/data-layer.type';

import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    provideAnimations(),
    provideState(successionFeature),
    provideEffects([...successionEffects]),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideStore(
      {
        router: routerReducer,
      },
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictStateSerializability: false,
          strictActionSerializability: false,
        },
      },
    ),
    environment.providers,
    provideRouterStore({
      stateKey: 'router',
    }),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      // turn this to `true` if you want to enable 'fondu enchaîné' effect when navigating.
      // withViewTransitions({ skipInitialTransition: true }),
    ),
    {
      provide: DATA_LAYER_SELECTORS_TOKEN,
      useFactory: (store: Store): DataLayerSelectors => ({
        selectDataLayerDocuments: store.select(selectDataLayerDocuments),
        selectDataLayerFuneralExpenses: store.select(selectDataLayerFuneralExpenses),
        selectDataLayerRelationshipWithDeceased: store.select(
          selectDataLayerRelationshipWithDeceased,
        ),
        selectDataLayerHasNotary: store.select(selectDataLayerHasNotary),
        selectDataLayerHasChildren: store.select(selectDataLayerHasChildren),
      }),
      deps: [Store],
    },
    provideDataLayer({
      serviceName: 'succession',
      prefix: '',
      // turn this property to `true` if you want to log warnings in the
      // console if some of your DataLayer objects
      // are formatted properly (only in development mode)
      // it needs the `prefix` property to be set in order to work
      checkDataLayerObjectsIntegrity: false,
    }),
    provideTranslateService({
      defaultLanguage: Language.FR,
      useDefaultLang: true,
      loader: {
        provide: TranslateLoader,
        useExisting: LabelsService,
      },
    }),
    provideLocaleId(),
    provideTypology(),
    provideTypologyFileUrl(() => {
      const context = inject(ContextService);
      return `/apps/ia-succession/labels/ia-succession-${context.language}-${context.typology}-V1.json`;
    }),

    provideAppInitializer(() => inject(LabelsService).getTranslation()),
    provideHttpClient(withInterceptorsFromDi(), withApimInterceptors()),
    provideJsonConvert(),
    provideOverlayContainer(),
  ],
};
